import React, { ChangeEvent, useEffect, useState, useRef } from 'react';
import { SearchBarProps } from '../../../types';
import Icon from '@mdi/react';
import { mdiMagnify, mdiTune } from '@mdi/js';
import './SearchBar.scss';

const SearchBar: React.FC<SearchBarProps> = ({ onSearch, onAdvancedToggle, resetToken, showAdvancedSearch = false }) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);
  const isInitialMount = useRef(true);

  function debounce<T extends any[]>(func: (...args: T) => void, delay: number): (...args: T) => void {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    return (...args: T) => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }
  

  // Use `React.useCallback` to memorize the debounced function
  const debouncedSearch = React.useCallback(debounce((query: string) => {
    fetchData(query);
  }, 500), []); // Empty dependency array to ensure stability across renders

  useEffect(() => {
    // Only trigger search if user has interacted with the search field, or if there's actual search text
    if (hasUserInteracted || searchTerm) {
      debouncedSearch(searchTerm);
    }
  }, [searchTerm, debouncedSearch, hasUserInteracted]); // Include `debouncedSearch` to adhere to exhaustive-deps rule

  const fetchData = (query: string) => {
    onSearch(query);
    // Implement your data fetching logic here
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setHasUserInteracted(true);
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    setSearchTerm('');
    setHasUserInteracted(true); // This will trigger the main useEffect to search with empty string
    // Don't call fetchData directly here - let the main useEffect handle it
  }, [resetToken]);

  const handleAdvancedToggle = () => {
    if (onAdvancedToggle) {
      onAdvancedToggle();
    }
  };

  return (
    <div className='search-bar-container'>
      <div className='search-bar'>
        <Icon className='search-icon' path={mdiMagnify} size={1} />
        <form>
          <input
            autoFocus 
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search..."
          />
        </form>
        <button 
          type="button"
          className={`advanced-toggle ${showAdvancedSearch ? 'active' : ''}`}
          onClick={handleAdvancedToggle}
          aria-label="Toggle advanced search"
        >
          <Icon path={mdiTune} size={0.9} />
          <span>Advanced</span>
        </button>
      </div>
    </div>
  );
};

export default SearchBar;
